import { useState } from 'react';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RegistrationModal = ({ isOpen, onClose }: RegistrationModalProps) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    handicapIndex: '',
    ghinNumber: '',
    gender: '',
    dateOfBirth: '',
    birthMonth: '',
    birthDay: '',
    birthYear: '',
    homeClub: '',
    howDidYouHear: '',
    shirtSize: '',
    preferredDate: 'April 10th, 2026',
    guestCount: '1',
    guestNames: '',
    specialRequests: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const availableDates = [
    'April 10th, 2026',
  ];

  const shirtSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
  const genders = ['Male', 'Female', 'Other', 'Prefer not to say'];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Update dateOfBirth when any birth field changes
      if (name === 'birthMonth' || name === 'birthDay' || name === 'birthYear') {
        const month = name === 'birthMonth' ? value : prev.birthMonth;
        const day = name === 'birthDay' ? value : prev.birthDay;
        const year = name === 'birthYear' ? value : prev.birthYear;
        
        if (month && day && year) {
          const monthNum = (months.indexOf(month) + 1).toString().padStart(2, '0');
          const dayNum = day.padStart(2, '0');
          updated.dateOfBirth = `${year}-${monthNum}-${dayNum}`;
        }
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Step 1: Send "payment pending" emails immediately (both player and admin)
      console.log('Step 1: Sending pending emails...');
      console.log('Email endpoint:', `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-riviera-pending-email`);
      console.log('Form data being sent:', formData);
      
      const emailResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-riviera-pending-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(formData),
        }
      );

      console.log('Email response status:', emailResponse.status);
      const emailData = await emailResponse.json();
      console.log('Email response data:', emailData);

      if (!emailResponse.ok) {
        console.error('❌ Failed to send pending emails:', emailData);
        // Show warning but continue to checkout
        alert(`Warning: Email notification may not have been sent. Error: ${emailData.error || 'Unknown error'}. Your registration will still proceed.`);
      } else {
        console.log('✅ Pending emails sent successfully!');
      }

      // Step 2: Create Stripe checkout session with all registration data
      console.log('Step 2: Creating Stripe checkout session...');
      const checkoutResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-riviera-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const checkoutData = await checkoutResponse.json();

      if (!checkoutResponse.ok) {
        throw new Error(checkoutData.error || 'Failed to create checkout session');
      }

      // Step 3: Redirect to Stripe Checkout
      if (checkoutData.url) {
        console.log('Step 3: Redirecting to Stripe checkout...');
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white w-full sm:rounded-lg sm:shadow-xl sm:max-w-4xl sm:my-8 min-h-screen sm:min-h-0 sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5 flex justify-between items-center z-10 shadow-sm">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Register for Riviera</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer touch-manipulation"
            aria-label="Close registration form"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6 sm:space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="bg-amber-50 border border-amber-300 px-4 py-3 rounded-lg flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-alarm-warning-line text-amber-600 text-lg"></i>
            </div>
            <p className="text-sm text-amber-800">
              <strong>Almost Gone!</strong> All other dates are sold out. <strong>April 10th, 2026</strong> is the only remaining date.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Registration Fee:</strong> $2,450 per person (includes greens fees, caddie, lunch, and open bar)
            </p>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">Contact Information</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                  autoComplete="email"
                  inputMode="email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Cell Phone *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">Address</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="addressLine1" className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  required
                  value={formData.addressLine1}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                  autoComplete="address-line1"
                />
              </div>

              <div>
                <label htmlFor="addressLine2" className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 2 (Optional)
                </label>
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                  autoComplete="address-line2"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    required
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    required
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    autoComplete="address-level1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Zip Code *
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    required
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    autoComplete="postal-code"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    autoComplete="country-name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Golf Information */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">Golf Information (Optional)</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="handicapIndex" className="block text-sm font-medium text-gray-700 mb-2">
                    Handicap Index
                  </label>
                  <input
                    type="text"
                    id="handicapIndex"
                    name="handicapIndex"
                    value={formData.handicapIndex}
                    onChange={handleChange}
                    placeholder="e.g., 12.5"
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    inputMode="decimal"
                  />
                </div>
                <div>
                  <label htmlFor="ghinNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    GHIN Number
                  </label>
                  <input
                    type="text"
                    id="ghinNumber"
                    name="ghinNumber"
                    value={formData.ghinNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base cursor-pointer touch-manipulation appearance-none bg-white"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                  >
                    <option value="">Select gender</option>
                    {genders.map((gender) => (
                      <option key={gender} value={gender}>
                        {gender}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <select
                      id="birthMonth"
                      name="birthMonth"
                      value={formData.birthMonth}
                      onChange={handleChange}
                      className="w-full px-2 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer touch-manipulation appearance-none bg-white"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.25rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.25em 1.25em" }}
                    >
                      <option value="">Month</option>
                      {months.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <select
                      id="birthDay"
                      name="birthDay"
                      value={formData.birthDay}
                      onChange={handleChange}
                      className="w-full px-2 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer touch-manipulation appearance-none bg-white"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.25rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.25em 1.25em" }}
                    >
                      <option value="">Day</option>
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                    <select
                      id="birthYear"
                      name="birthYear"
                      value={formData.birthYear}
                      onChange={handleChange}
                      className="w-full px-2 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer touch-manipulation appearance-none bg-white"
                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.25rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.25em 1.25em" }}
                    >
                      <option value="">Year</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="homeClub" className="block text-sm font-medium text-gray-700 mb-2">
                  Home Club
                </label>
                <input
                  type="text"
                  id="homeClub"
                  name="homeClub"
                  value={formData.homeClub}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                />
              </div>

              <div>
                <label htmlFor="howDidYouHear" className="block text-sm font-medium text-gray-700 mb-2">
                  How did you hear about us?
                </label>
                <input
                  type="text"
                  id="howDidYouHear"
                  name="howDidYouHear"
                  value={formData.howDidYouHear}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base touch-manipulation"
                />
              </div>

              <div>
                <label htmlFor="shirtSize" className="block text-sm font-medium text-gray-700 mb-2">
                  Shirt Size
                </label>
                <select
                  id="shirtSize"
                  name="shirtSize"
                  value={formData.shirtSize}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base cursor-pointer touch-manipulation appearance-none bg-white"
                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                >
                  <option value="">Select size</option>
                  {shirtSizes.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Event Details */}
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">Event Details</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <div className="w-full px-4 py-3.5 border border-green-400 bg-green-50 rounded-lg text-base font-semibold text-green-800 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-green-600"></i>
                  April 10th, 2026
                  <span className="ml-auto text-xs font-normal text-green-600 bg-green-100 px-2 py-1 rounded-full">Last Available Date</span>
                </div>
                <input type="hidden" name="preferredDate" value="April 10th, 2026" />
                <p className="text-xs text-gray-500 mt-1">All other dates are sold out. April 10th is the only remaining spot.</p>
              </div>

              <div>
                <label htmlFor="guestCount" className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Players (including yourself) *
                </label>
                <select
                  id="guestCount"
                  name="guestCount"
                  required
                  value={formData.guestCount}
                  onChange={handleChange}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base cursor-pointer touch-manipulation appearance-none bg-white"
                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 0.5rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                >
                  <option value="1">1 Player ($2,450)</option>
                  <option value="2">2 Players ($4,900)</option>
                  <option value="3">3 Players ($7,350)</option>
                </select>
              </div>

              {parseInt(formData.guestCount) > 1 && (
                <div>
                  <label htmlFor="guestNames" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Player Names *
                  </label>
                  <textarea
                    id="guestNames"
                    name="guestNames"
                    required
                    value={formData.guestNames}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Please list the names of additional players (one per line)"
                    className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base resize-none touch-manipulation"
                  ></textarea>
                </div>
              )}

              <div>
                <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-2">
                  Special Requests or Dietary Restrictions (Max 500 characters)
                </label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder="Any special accommodations, dietary restrictions, or requests..."
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base resize-none touch-manipulation"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.specialRequests.length}/500 characters</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 px-4 py-3 rounded-lg">
            <p className="text-base font-semibold text-gray-800 mb-1">
              Total Amount: ${(2450 * parseInt(formData.guestCount)).toLocaleString()}
            </p>
            <p className="text-xs text-gray-600">
              You will be redirected to Stripe for secure payment processing. Cancellations allowed up to one week before the event.
            </p>
          </div>

          <div className="flex flex-col gap-3 pt-4 pb-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-800 hover:bg-blue-900 text-white px-6 py-4 text-lg font-semibold rounded-lg transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            >
              {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full px-6 py-4 border-2 border-gray-300 text-gray-700 text-lg font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationModal;
